import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { receiptService } from '../../services/ReceiptService';
import { downloadReceiptPDF, printReceipt } from '../../utils/PDFExport';
import { ArrowDownIcon, PrinterIcon, ArrowLeftIcon } from '@heroicons/react/outline';

const ReceiptView = ({ receiptId: receiptIdProp, userType }) => {
  const { id: receiptIdParam } = useParams();
  const receiptId = receiptIdProp || receiptIdParam;
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const result = await receiptService.getReceipt(receiptId);
        if (result.success) {
          setReceipt(result.receipt);
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError('Failed to load receipt');
      } finally {
        setLoading(false);
      }
    };

    if (receiptId) {
      fetchReceipt();
    }
  }, [receiptId]);

  if (loading) return <div className="p-8 text-center">Loading receipt...</div>;
  if (error) return <div className="p-8 text-center text-red-600">{error}</div>;
  if (!receipt) return <div className="p-8 text-center">Receipt not found</div>;

  const formattedDate = receipt.createdAt?.toDate?.() 
    ? receipt.createdAt.toDate().toLocaleDateString()
    : new Date(receipt.createdAt).toLocaleDateString();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(userType === 'MPS' ? '/mps/receipts' : '/eo/receipts')}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Receipt</h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => printReceipt('receipt-content')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PrinterIcon className="h-5 w-5" />
            Print
          </button>
          <button
            onClick={() => downloadReceiptPDF(receipt)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <ArrowDownIcon className="h-5 w-5" />
            Export PDF
          </button>
        </div>
      </div>

      <div id="receipt-content" className="bg-white rounded-lg border border-gray-200 p-8">
        {/* Header */}
        <div className="mb-8 pb-8 border-b-2 border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">PEBBLE</h2>
          <p className="text-gray-600">Event Marketplace</p>
        </div>

        {/* Receipt Details */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <p className="text-sm text-gray-600 font-semibold mb-2">RECEIPT #</p>
            <p className="text-lg font-mono">{receipt.transactionId}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600 font-semibold mb-2">DATE</p>
            <p className="text-lg">{formattedDate}</p>
          </div>
        </div>

        {/* Event & Party Details */}
        <div className="grid grid-cols-2 gap-8 mb-8 pb-8 border-b border-gray-200">
          <div>
            <p className="text-sm text-gray-600 font-semibold mb-4">FROM</p>
            <div className="space-y-2">
              <p className="font-semibold text-gray-900">{receipt.mpsBusinessName}</p>
              <p className="text-sm text-gray-600">{receipt.mpsEmail}</p>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 font-semibold mb-4">TO</p>
            <div className="space-y-2">
              <p className="font-semibold text-gray-900">{receipt.eoName}</p>
              <p className="text-sm text-gray-600">Event: {receipt.eventName}</p>
            </div>
          </div>
        </div>

        {/* Fee Breakdown */}
        <div className="mb-8">
          <p className="text-sm text-gray-600 font-semibold mb-4">PAYMENT BREAKDOWN</p>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-700">Vendor Registration Fee</span>
              <span className="font-mono font-semibold">${(receipt.totalAmount / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-red-600">
              <span>Pebble Service Fee (15%)</span>
              <span className="font-mono font-semibold">-${(receipt.platformFee / 100).toFixed(2)}</span>
            </div>
            <div className="pt-3 border-t-2 border-gray-200 flex justify-between">
              <span className="font-semibold text-gray-900">Event Organizer Receives</span>
              <span className="font-mono font-bold text-lg">${(receipt.eoAmount / 100).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>Thank you for your business!</p>
          <p className="mt-2">Transaction ID: {receipt.paymentIntentId}</p>
        </div>
      </div>
    </div>
  );
};

export default ReceiptView;
